/**
 * Fixed, printed-form content for the A4 ticket check-in sheet.
 *
 * These are the static strings/values that appear on every sheet regardless of
 * ticket. They live here (rather than inline in the component) so the workshop
 * can tweak the printed labels/prices in one place without touching layout.
 * There is intentionally no admin editor for these (see the feature plan).
 *
 * ⚠️ Confirm these values are current before rollout — extracted from the two
 * scanned example sheets (tickets 8261205 / 8260945).
 */

/** Logo shown top-right of the sheet. Black transparent-bg wordmark prints
 *  crisply on the white sheet; `SHEET_BRAND` is its alt text. */
export const SHEET_BRAND = 'TechSolvers';
export const SHEET_LOGO_SRC = '/black.logo.transparent.bg.800w.png';

/** RECEPTION CHECK-IN INSPECTION — "ITEM - DAMAGE" rows (hand-filled). */
export const RECEPTION_DAMAGE_ROWS = [
  'Display',
  'Display bezel',
  'Display chassis',
  'Hinges',
  'Palmrest',
  'Bottom chassis',
] as const;

/** RECEPTION CHECK-IN INSPECTION — "ITEM - STATUS" rows (hand-filled). */
export const RECEPTION_STATUS_ROWS = [
  'All feet present?',
  'All screws present?',
  'Wobbles?',
  'USB Item(s)?',
  'Power adapter(s)',
  'Update Serial in RS?',
] as const;

/** The printed italic guide row at the top of the damage table. */
export const RECEPTION_EXAMPLE_ROW = {
  item: 'Example',
  notes: 'Right hinge compromised, screw fittings loose',
} as const;

/** TECHNICIAN INSPECTION — task rows with PRE / POST / NOTES columns. */
export const TECHNICIAN_INSPECTION_ROWS = ['Exterior', 'Interior'] as const;

/** "TP" codes row. */
export const TP_CODES = ['SCRNZ', 'ITT', 'CTPC', 'QR', 'OT'] as const;

/** DIAGNOSTICS — "TYPE" options (retail variant only). */
export const DIAGNOSTIC_TYPE_OPTIONS = [
  { label: 'Data Recovery', price: '$45' },
  { label: 'Laptop', price: '$90' },
  { label: 'Desktop / AIO', price: '$135' },
] as const;

/** DIAGNOSTICS — "SERVICE" options (retail variant only). */
export const DIAGNOSTIC_SERVICE_OPTIONS = [
  { label: 'Pre Booked', price: '+$30' },
  { label: 'Expedited', price: '+$60' },
  { label: 'Emergency', price: '+$120' },
] as const;

/**
 * Fallback pre-printed prefix on the invoice-id fields (year only). Used when
 * the live latest-invoice lookup is unavailable; normally the sheet prints the
 * first 4 digits of the current invoice sequence instead (see
 * `useLatestInvoicePrefix` / `TicketSheetData.invoiceIdPrefix`).
 */
export const INVOICE_ID_PREFIX = '26';

/** Banner text that replaces the pricing boxes for monthly-invoice customers. */
export const MONTHLY_INVOICE_NOTICE = 'TO BE BILLED ON MONTHLY INVOICE';

/** COLLECTION box instruction + circled notice methods. */
export const COLLECTION_NOTICE =
  'Circle notice method and capture date & time customer advised to collect device and 30 day disposal policy';
export const COLLECTION_METHODS = ['PHONE', 'EMAIL', 'SMS', 'VM'] as const;

/** Footer line (the ticket number is interpolated by the component). */
export const PASSWORDS_FOOTER = 'PASSWORDS - DESTROY UPON JOB COMPLETION';
